import {LoyaltyCard} from "@/loyalty-card/interfaces/hds/LoyaltyCard";
import {LoyaltyCardObjective} from "@/loyalty-card/interfaces/LoyaltyCard";

/** The reward model of a loyalty card. */
export type LoyaltyCardModel = "product" | "point" | "both";

/**
 * The `fill` filter ids accepted by the memberships listing for the four
 * card-fill quartiles. Historical labels of a 10-stamp card, kept as opaque
 * aliases: the server maps them to real bounds from the card threshold.
 */
export const LOYALTY_CARD_FILL_QUARTILE_IDS = ["0-2", "3-5", "6-8", "9-10"] as const;

/** A card-fill quartile: the filter id to send and its inclusive bounds. */
export interface LoyaltyCardFillQuartile {
    id: typeof LOYALTY_CARD_FILL_QUARTILE_IDS[number];
    min: number;
    max: number;
}

/** Threshold assumed when the card declares no objective (same as the server). */
const DEFAULT_FILL_THRESHOLD = 10;

/** The kind of reward a loyalty card grants, for wording purposes. */
export type LoyaltyCardRewardKind = "product" | "point" | "discount";

/**
 * Service for loyalty cards.
 */
const LoyaltyCardService = () => {

    /**
     * Determine if a loyalty card can have point rewards.
     */
    const hasPointRewards = (loyaltyCard: Pick<LoyaltyCard, "point_rewards_count">): boolean => {
        return !!loyaltyCard.point_rewards_count;
    };

    /**
     * Determine if a loyalty card can have product rewards.
     */
    const hasProductRewards = (loyaltyCard: {product_rewards_count: number}): boolean => {
        return !!loyaltyCard.product_rewards_count;
    };

    /**
     * Resolve the reward model of a loyalty card, so every screen decides
     * the product/point precedence the same way. Defaults to "product"
     * when the card declares no reward at all.
     */
    const resolveCardModel = (loyaltyCard: Pick<LoyaltyCard, "point_rewards_count" | "product_rewards_count">): LoyaltyCardModel => {
        if (hasProductRewards(loyaltyCard) && hasPointRewards(loyaltyCard)) {
            return "both";
        }

        return hasPointRewards(loyaltyCard) ? "point" : "product";
    };

    /**
     * Determine if a loyalty card grants euro-discount rewards, from its
     * objectives (no dedicated counter is served for this reward type).
     */
    const hasDiscountRewards = (loyaltyCard: {objectives?: LoyaltyCardObjective[]}): boolean => {
        return (loyaltyCard.objectives ?? []).some(objective => objective.reward?.type?.slug === "discount");
    };

    /**
     * Resolve the kind of reward the card grants, for display wording.
     * Product wins over points (same precedence as {@link resolveCardModel}),
     * and euro discounts are detected from the objectives.
     */
    const resolveRewardKind = (
        loyaltyCard: Pick<LoyaltyCard, "point_rewards_count" | "product_rewards_count"> & {objectives?: LoyaltyCardObjective[]}
    ): LoyaltyCardRewardKind => {
        if (resolveCardModel(loyaltyCard) === "point") {
            return "point";
        }

        // "product" or "both": product wording wins; a card with neither
        // counter may still grant euro discounts (detected from objectives).
        return hasProductRewards(loyaltyCard) || !hasDiscountRewards(loyaltyCard)
            ? "product"
            : "discount";
    };

    /**
     * The unit in which the card objectives count progress: products for
     * "quantity" objectives, euros for "amount" objectives.
     */
    const objectiveUnitLabel = (loyaltyCard: {objectives?: LoyaltyCardObjective[]}): "produit" | "€" => {
        const slug = (loyaltyCard.objectives ?? []).find(objective => objective.type)?.type?.slug;

        return slug === "amount" ? "€" : "produit";
    };

    /**
     * The card threshold the fill quartiles are derived from: the card's
     * first tier (smallest positive objective value), which is the objective
     * the membership fill is displayed against on a card mixing tiers
     * (5 products, 100 €…). {@link DEFAULT_FILL_THRESHOLD} when the card has
     * no positive objective. Mirrors the server's fill filter resolution.
     */
    const resolveFillThreshold = (loyaltyCard: {objectives?: LoyaltyCardObjective[]}): number => {
        const values = (loyaltyCard.objectives ?? [])
            .map(objective => Math.trunc(objective.value ?? 0))
            .filter(value => value >= 1);

        return values.length ? Math.min(...values) : DEFAULT_FILL_THRESHOLD;
    };

    /**
     * Split a card threshold into its fill quartiles (step = ceil(threshold / 4)),
     * dropping the quartiles whose floor exceeds the threshold. Replays the
     * server's bucket bounds so the listing filters read the same bounds the
     * API applies: 10 → 0-2 / 3-5 / 6-8 / 9-10, 5 → 0-1 / 2-3 / 4-5,
     * 35 → 0-8 / 9-17 / 18-26 / 27-35.
     */
    const fillQuartiles = (threshold: number): LoyaltyCardFillQuartile[] => {
        const step = Math.max(1, Math.ceil(threshold / 4));
        const last = LOYALTY_CARD_FILL_QUARTILE_IDS.length - 1;

        return LOYALTY_CARD_FILL_QUARTILE_IDS
            .map((id, index) => ({
                id,
                min: index * step,
                max: index === last ? threshold : Math.min(threshold, (index + 1) * step - 1)
            }))
            .filter(quartile => quartile.min <= threshold);
    };

    return {
        hasPointRewards,
        hasProductRewards,
        hasDiscountRewards,
        resolveCardModel,
        resolveRewardKind,
        objectiveUnitLabel,
        resolveFillThreshold,
        fillQuartiles
    };
};

export default LoyaltyCardService;
